import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

import Icon from "@ats/src/components/shared/Icon";
import Button from "@ats/src/components/shared/Button";

import { PlatoMark, PlatoChip } from "@ats/src/components/shared/PlatoMark";

export type JobApplicationTabEmptyStateStatus =
  | "ready"
  | "processing"
  | "failed"
  | "noCredits"
  | "noResume"
  | "bulkQueued";

export interface JobApplicationTabEmptyStateProps {
  icon?: string;
  title?: string;
  message?: React.ReactNode;
  buttonLabel?: string;
  buttonIcon?: string;
  buttonLink?: string;
  buttonDisabled?: boolean;
  onClick?: () => void;
  footnote?: React.ReactNode;
  borderless?: boolean;
  roomy?: boolean;
  children?: React.ReactNode;
}

const JobApplicationTabEmptyState: React.FunctionComponent<JobApplicationTabEmptyStateProps> = (props) => {
  const buttonProps: any = {
    styleType: "primary",
    disabled: props.buttonDisabled,
  };
  if (props.buttonLink) {
    buttonProps.type = "internalLink";
    buttonProps.link = props.buttonLink;
  } else {
    buttonProps.onClick = props.onClick;
  }

  return (
    <Styled.Container borderless={props.borderless} roomy={props.roomy}>
      {props.icon && (
        <Styled.IconWrapper>
          {props.icon === "plato" ? <PlatoChip size={40} /> : <Icon name={props.icon} />}
        </Styled.IconWrapper>
      )}
      {props.title && <h2>{props.title}</h2>}
      {props.message && <p>{props.message}</p>}
      {(props.buttonLabel || props.footnote || props.children) && (
        <Styled.Actions roomy={props.roomy}>
          {props.buttonLabel && (
            <Button {...buttonProps}>
              <Styled.ButtonContent>
              {props.buttonIcon && (
                props.buttonIcon === "plato" ? <PlatoMark size={16} /> : <Icon name={props.buttonIcon} />
              )}
              {props.buttonLabel}
            </Styled.ButtonContent>
            </Button>
          )}
          {props.footnote && <Styled.Footnote>{props.footnote}</Styled.Footnote>}
          {props.children}
        </Styled.Actions>
      )}
    </Styled.Container>
  );
};

export default JobApplicationTabEmptyState;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Container = styled.div(({ theme, borderless, roomy }: any) => {
  const t: any = theme;
  return css`
    label: JobApplicationTabEmptyState;
    ${[t.px(6), t.rounded.md]}
    ${roomy ? t.py(10) : t.py(5)}
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    color: ${t.dark ? t.color.gray[200] : t.color.black};

    ${!borderless && css`
      border: 1px solid ${t.dark ? "transparent" : t.color.gray[200]};
      background-color: ${t.dark ? "rgba(255, 255, 255, 0.07)" : "transparent"};
    `}

    h2 {
      ${[t.text.h5, t.mt(1), t.mb(1)]}
    }
    p {
      ${t.text.sm}
      max-width: 24rem;
      line-height: 1.45;
      color: ${t.dark ? t.color.gray[400] : t.color.gray[600]};

      a {
        ${t.text.bold}
        color: inherit;
        &:hover {
          text-decoration: underline;
        }
      }
    }
  `;
});

Styled.IconWrapper = styled.div((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationTabEmptyState_IconWrapper;
    ${t.mb(2)}
    display: inline-flex;
    color: ${t.dark ? t.color.gray[400] : t.color.gray[500]};

    svg {
      width: 24px;
      height: 24px;
    }
  `;
});

Styled.Actions = styled.div(({ theme, roomy }: any) => {
  const t: any = theme;
  return css`
    label: JobApplicationTabEmptyState_Actions;
    ${roomy ? t.mt(8) : t.mt(4)}
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${t.spacing[2]};
  `;
});

Styled.ButtonContent = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationTabEmptyState_ButtonContent;
    display: flex;
    align-items: center;

    svg {
      width: 18px;
      height: 18px;
      margin-right: 6px;
      margin-top: -2px;
    }
  `;
});

Styled.Footnote = styled.span((props: any) => {
  const t: any = props.theme;
  return css`
    label: JobApplicationTabEmptyState_Footnote;
    ${t.text.xs}
    color: ${t.dark ? t.color.gray[400] : t.color.gray[500]};
    font-variant-numeric: tabular-nums;
  `;
});
